import React from "react";
import { Box, Chip, Paper, Typography, useTheme } from "@mui/material";
import CheckCircleRoundedIcon from "@mui/icons-material/CheckCircleRounded";
import ErrorRoundedIcon from "@mui/icons-material/ErrorRounded";
import WarningRoundedIcon from "@mui/icons-material/WarningRounded";
import HelpOutlineRoundedIcon from "@mui/icons-material/HelpOutlineRounded";

/**
 * Shared building blocks for the Server screen (2026-10-21).
 *
 * Each Server tab had grown its own card, chip and filter styling, and they
 * drifted apart: sharp tab indicators, filter buttons welded to the tab bar,
 * three different "status chip" implementations with three different radii.
 * Everything in this screen now renders through these primitives so the page
 * matches the rest of the Portal — 12px radii, soft tinted chips, one spacing
 * scale — and so the tabs cannot drift again.
 */

export const CARD_RADIUS = "12px";

// ── Formatters ──────────────────────────────────────────────────────────────

export const formatPercent = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(1)}%`;

export const formatBytes = (bytes) => {
  const value = Number(bytes);
  if (!Number.isFinite(value) || value <= 0) return "—";
  if (value < 1024) return `${value} B`;
  if (value < 1024 * 1024) return `${(value / 1024).toFixed(1)} KB`;
  if (value < 1024 * 1024 * 1024) return `${(value / 1024 / 1024).toFixed(1)} MB`;
  return `${(value / 1024 / 1024 / 1024).toFixed(2)} GB`;
};

export const formatGB = (value) =>
  value === null || value === undefined ? "—" : `${Number(value).toFixed(1)} GB`;

export const formatMB = (value) =>
  value === null || value === undefined ? "—" : `${Math.round(Number(value))} MB`;

export const formatCount = (value) => {
  const numeric = Number(value);
  if (!Number.isFinite(numeric)) return "—";
  return numeric.toLocaleString();
};

export const formatDuration = (seconds) => {
  if (seconds === null || seconds === undefined) return "—";
  const total = Math.floor(Number(seconds));
  const days = Math.floor(total / 86400);
  const hours = Math.floor((total % 86400) / 3600);
  const minutes = Math.floor((total % 3600) / 60);
  if (days > 0) return `${days}d ${hours}h`;
  if (hours > 0) return `${hours}h ${minutes}m`;
  return `${minutes}m`;
};

export const relativeTime = (isoValue) => {
  if (!isoValue) return "never";
  const seconds = Math.max(
    0,
    Math.floor((Date.now() - new Date(isoValue).getTime()) / 1000)
  );
  if (seconds < 60) return `${seconds}s ago`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m ago`;
  if (seconds < 86400) return `${Math.floor(seconds / 3600)}h ago`;
  return `${Math.floor(seconds / 86400)}d ago`;
};

export const formatDateTime = (isoValue) =>
  isoValue ? new Date(isoValue).toLocaleString() : "—";

// ── Severity / status tokens ────────────────────────────────────────────────

/**
 * One place that decides what "good" looks like.
 * Severities come from the collector: ok | warning | critical | unknown.
 */
export const severityToken = (theme, severity) => {
  if (severity === "critical") {
    return { color: theme.palette.error.main, Icon: ErrorRoundedIcon, soft: "0d" };
  }
  if (severity === "warning") {
    return { color: theme.palette.warning.main, Icon: WarningRoundedIcon, soft: "0d" };
  }
  if (severity === "ok") {
    return { color: theme.palette.success.main, Icon: CheckCircleRoundedIcon, soft: "0d" };
  }
  return { color: theme.palette.grey[500], Icon: HelpOutlineRoundedIcon, soft: "0d" };
};

/**
 * Maps the many status vocabularies in this screen (systemd states, backup
 * states, certificate states, health states) onto four visual tones.
 */
const STATUS_TONE = {
  running: "ok",
  ok: "ok",
  valid: "ok",
  online: "ok",
  unlimited: "ok",
  warning: "warning",
  aging: "warning",
  stale: "warning",
  empty: "warning",
  unknown: "warning",
  unconfigured: "warning",
  stopped: "critical",
  critical: "critical",
  expired: "critical",
  error: "critical",
  unavailable: "critical",
  missing: "critical",
};

export const StatusChip = ({ status, label, size = "small" }) => {
  const theme = useTheme();
  const { color, Icon } = severityToken(theme, STATUS_TONE[status] || "unknown");

  return (
    <Chip
      size={size}
      icon={<Icon />}
      label={label || status || "unknown"}
      sx={{
        height: 24,
        borderRadius: "8px",
        fontWeight: 600,
        fontSize: "0.72rem",
        textTransform: "capitalize",
        bgcolor: `${color}1a`,
        color,
        "& .MuiChip-icon": { color, fontSize: 15 },
      }}
    />
  );
};

// ── Layout primitives ───────────────────────────────────────────────────────

/** The single card used everywhere on this screen. */
export const Panel = ({
  title,
  subtitle,
  icon,
  action,
  accent,
  dense = false,
  children,
  sx,
}) => {
  const theme = useTheme();
  const { color } = severityToken(theme, accent);

  return (
    <Paper
      variant="outlined"
      sx={{
        p: dense ? 1.5 : 2,
        borderRadius: CARD_RADIUS,
        bgcolor: accent ? `${color}0d` : theme.palette.background.paper,
        borderColor: accent ? `${color}55` : undefined,
        display: "flex",
        flexDirection: "column",
        gap: dense ? 1 : 1.5,
        height: "100%",
        minWidth: 0,
        ...sx,
      }}
    >
      {(title || action) && (
        <Box sx={{ display: "flex", alignItems: "flex-start", gap: 1, minWidth: 0 }}>
          {icon && (
            <Box
              sx={{
                display: "flex",
                color: accent ? color : theme.palette.primary.main,
                mt: "1px",
                flexShrink: 0,
              }}
            >
              {icon}
            </Box>
          )}

          <Box sx={{ minWidth: 0, flex: 1 }}>
            <Typography variant="subtitle2" sx={{ fontWeight: 700, lineHeight: 1.3 }}>
              {title}
            </Typography>
            {subtitle && (
              <Typography
                variant="caption"
                color="text.secondary"
                component="div"
                sx={{ lineHeight: 1.4 }}
              >
                {subtitle}
              </Typography>
            )}
          </Box>

          {action && <Box sx={{ flexShrink: 0 }}>{action}</Box>}
        </Box>
      )}

      {children}
    </Paper>
  );
};

/**
 * Label / value pair used by the Setup tab and the host card.
 * `tone` lets a row flag itself without inventing a new chip.
 */
export const DataRow = ({ label, value, tone, mono = false }) => {
  const theme = useTheme();
  const { color } = severityToken(theme, tone);

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        gap: 1.5,
        py: 0.75,
        borderBottom: `1px dashed ${theme.palette.divider}`,
        "&:last-of-type": { borderBottom: "none", pb: 0 },
      }}
    >
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ minWidth: 128, flexShrink: 0, pt: "1px" }}
      >
        {label}
      </Typography>
      <Typography
        variant="body2"
        sx={{
          flex: 1,
          minWidth: 0,
          wordBreak: "break-word",
          fontWeight: tone ? 600 : 400,
          color: tone ? color : theme.palette.text.primary,
          fontFamily: mono ? "monospace" : undefined,
          fontSize: mono ? "0.78rem" : undefined,
        }}
      >
        {value}
      </Typography>
    </Box>
  );
};

/** Compact KPI used inside a Panel (no card chrome of its own). */
export const StatTile = ({ label, value, hint, color }) => {
  const theme = useTheme();

  return (
    <Box sx={{ minWidth: 0 }}>
      <Typography variant="caption" color="text.secondary" component="div">
        {label}
      </Typography>
      <Typography
        variant="subtitle1"
        sx={{
          fontWeight: 700,
          lineHeight: 1.2,
          color: color || theme.palette.text.primary,
          fontVariantNumeric: "tabular-nums",
        }}
      >
        {value}
      </Typography>
      {hint && (
        <Typography variant="caption" color="text.secondary" component="div" noWrap>
          {hint}
        </Typography>
      )}
    </Box>
  );
};

/**
 * Rounded segmented control — replaces the ToggleButtonGroup that used to sit
 * flush against the tab bar looking like part of it.
 */
export const SegmentedControl = ({ value, onChange, options, ariaLabel }) => {
  const theme = useTheme();

  return (
    <Box
      role="group"
      aria-label={ariaLabel}
      sx={{
        display: "inline-flex",
        alignItems: "center",
        gap: "2px",
        p: "3px",
        borderRadius: "10px",
        border: `1px solid ${theme.palette.divider}`,
        bgcolor: theme.palette.background.default,
        flexShrink: 0,
      }}
    >
      {options.map((option) => {
        const selected = option.value === value;

        return (
          <Box
            key={option.value}
            component="button"
            type="button"
            aria-pressed={selected}
            onClick={() => onChange(option.value)}
            sx={{
              appearance: "none",
              border: "none",
              background: "transparent",
              cursor: "pointer",
              fontFamily: "inherit",
              fontSize: "0.8rem",
              fontWeight: selected ? 700 : 600,
              px: 1.5,
              py: 0.5,
              borderRadius: "8px",
              color: selected
                ? theme.palette.primary.main
                : theme.palette.text.secondary,
              bgcolor: selected ? `${theme.palette.primary.main}1a` : "transparent",
              transition: "background-color 0.18s ease, color 0.18s ease",
              "&:hover": {
                bgcolor: selected
                  ? `${theme.palette.primary.main}26`
                  : theme.palette.action.hover,
                color: selected
                  ? theme.palette.primary.main
                  : theme.palette.text.primary,
              },
              "&:focus-visible": {
                outline: `2px solid ${theme.palette.primary.main}`,
                outlineOffset: 1,
              },
            }}
          >
            {option.label}
          </Box>
        );
      })}
    </Box>
  );
};

/** Horizontal row above a list/table — search, filters, actions. */
export const Toolbar = ({ children, sx }) => {
  const theme = useTheme();

  return (
    <Paper
      variant="outlined"
      sx={{
        p: 1,
        pl: 1.5,
        borderRadius: CARD_RADIUS,
        display: "flex",
        alignItems: "center",
        gap: 1,
        flexWrap: "wrap",
        bgcolor: theme.palette.background.paper,
        ...sx,
      }}
    >
      {children}
    </Paper>
  );
};

/** Quiet placeholder — one style instead of five different paragraphs. */
export const EmptyNote = ({ children, icon }) => {
  const theme = useTheme();

  return (
    <Box
      sx={{
        display: "flex",
        alignItems: "center",
        gap: 1,
        py: 1.5,
        color: theme.palette.text.secondary,
      }}
    >
      {icon && <Box sx={{ display: "flex", opacity: 0.7 }}>{icon}</Box>}
      <Typography variant="body2" color="text.secondary">
        {children}
      </Typography>
    </Box>
  );
};

/**
 * Dependency-free sparkline. Kept here (not in Overview) because both the
 * Stats tab and the History summary use it.
 */
export const Sparkline = ({ values, height = 34, color }) => {
  const theme = useTheme();
  const strokeColor = color || theme.palette.primary.main;

  const points = (values || []).filter(
    (value) => value !== null && value !== undefined
  );

  if (points.length < 2) return null;

  const width = 100;
  const max = 100;
  const min = 0;

  const geometry = points
    .map((value, index) => {
      const x = (index / (points.length - 1)) * width;
      const y = height - ((Number(value) - min) / (max - min)) * height;
      return `${x.toFixed(2)},${Math.max(0, Math.min(height, y)).toFixed(2)}`;
    })
    .join(" ");

  return (
    <Box sx={{ width: "100%", lineHeight: 0 }}>
      <svg
        viewBox={`0 0 ${width} ${height}`}
        preserveAspectRatio="none"
        style={{ width: "100%", height }}
        aria-hidden="true"
      >
        <polyline
          points={geometry}
          fill="none"
          stroke={strokeColor}
          strokeWidth="1.5"
          vectorEffect="non-scaling-stroke"
          strokeLinejoin="round"
          strokeLinecap="round"
        />
      </svg>
    </Box>
  );
};
